/**
 * Shared modal shell - use this for every new/converted modal so they all
 * look consistent: rounded card, clean header, optional soft-panel content
 * area, and a footer with Cancel (outline pill) + Primary action (solid
 * pill) right-aligned - matching the reference "Delete browsing data" style
 * dialog. Built on the existing --fc-* design tokens, not new hardcoded hex.
 *
 * Usage:
 *   <Modal title="Remove candidate" onClose={close} maxWidth={480}
 *     footer={<ModalFooter onCancel={close} onConfirm={doRemove} confirmLabel="Remove" confirmTone="danger" />}>
 *     <ModalPanel>
 *       <ModalCheckRow checked={a} onChange={setA} label="Notify the candidate" sub="Sends an email" />
 *     </ModalPanel>
 *   </Modal>
 */

export function Modal({ title, onClose, children, footer, maxWidth = 520 }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ position:"absolute", inset:0, background:"rgba(15,23,42,0.55)", backdropFilter:"blur(6px)" }} onClick={onClose} />
      <div style={{ position:"relative", background:"var(--fc-card-bg)", borderRadius:"var(--fc-radius-2xl)", width:"100%", maxWidth, boxShadow:"0 24px 80px rgba(0,0,0,0.18)", zIndex:1, overflow:"hidden", display:"flex", flexDirection:"column", maxHeight:"90vh" }}>

        <div style={{ padding:"20px 24px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ fontSize:17, fontWeight:700, color:"var(--fc-text-primary)" }}>{title}</div>
          {onClose && (
            <button onClick={onClose} aria-label="Close"
              style={{ width:30, height:30, borderRadius:"50%", border:"none", background:"transparent", cursor:"pointer", color:"var(--fc-text-muted)", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--fc-bg-light)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>

        <div style={{ padding:"0 24px 20px", overflowY:"auto", flex:1 }}>
          {children}
        </div>

        {footer && (
          <div style={{ padding:"16px 24px", borderTop:"1px solid var(--fc-border-light)", flexShrink:0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Soft rounded panel that groups related content/options - matches the light blue-gray box in the reference. */
export function ModalPanel({ children }) {
  return (
    <div style={{ background:"var(--fc-bg-light)", borderRadius:"var(--fc-radius-lg)", overflow:"hidden" }}>
      {children}
    </div>
  );
}

/** A single labeled row inside a ModalPanel - e.g. a checkbox option with a title + subtext. */
export function ModalCheckRow({ checked, onChange, label, sub, last }) {
  return (
    <label style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px 16px", cursor: onChange ? "pointer" : "default", borderBottom: last ? "none" : "1px solid var(--fc-border-light)" }}>
      <input type="checkbox" checked={checked} onChange={e => onChange?.(e.target.checked)}
        style={{ width:18, height:18, marginTop:1, accentColor:"var(--fc-primary)", cursor: onChange ? "pointer" : "default", flexShrink:0 }} />
      <div>
        <div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-primary)" }}>{label}</div>
        {sub && <div style={{ fontSize:12.5, color:"var(--fc-text-muted)", marginTop:2 }}>{sub}</div>}
      </div>
    </label>
  );
}

/**
 * Footer button row: Cancel (outline pill) + primary action (solid pill),
 * right-aligned, matching the reference. Optional left slot for identity/
 * meta info (e.g. avatar + name), same layout as the reference's account row.
 */
export function ModalFooter({ onCancel, cancelLabel = "Cancel", onConfirm, confirmLabel = "Confirm", confirmTone = "primary", confirmDisabled, confirmBusy, left }) {
  const confirmColor = confirmTone === "danger" ? "var(--fc-red)" : "var(--fc-primary)";
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent: left ? "space-between" : "flex-end", gap:12 }}>
      {left && <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>{left}</div>}
      <div style={{ display:"flex", gap:10, flexShrink:0 }}>
        {onCancel && (
          <button onClick={onCancel}
            style={{ padding:"9px 18px", borderRadius:99, border:"1px solid var(--fc-border-strong)", background:"var(--fc-card-bg)", color:"var(--fc-text-secondary)", fontSize:13.5, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
            {cancelLabel}
          </button>
        )}
        {onConfirm && (
          <button onClick={onConfirm} disabled={confirmDisabled || confirmBusy}
            style={{ padding:"9px 20px", borderRadius:99, border:"none", background:confirmColor, color:"#fff", fontSize:13.5, fontWeight:700, cursor:(confirmDisabled||confirmBusy)?"default":"pointer", fontFamily:"inherit", opacity:(confirmDisabled||confirmBusy)?0.6:1, display:"flex", alignItems:"center", gap:7 }}>
            {confirmBusy && <i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} />}
            {confirmLabel}
          </button>
        )}
      </div>
    </div>
  );
}
